// Central source of truth for anything related to coins, power-ups, and monetization.
// Note: Costs are ultimately enforced server-side (Supabase Edge Functions / RPC).

import {
  DEFAULT_FREE_INSPECTS,
  DEFAULT_FREE_SKIPS,
  RIDDLE_COST,
  RIDDLE_EARLY_GUESS_SURCHARGE,
  RIDDLE_EASY_BONUS,
  RIDDLE_REPEAT_INCREMENT,
} from '../constants/games/unlimitedGame';

const toPositiveInt = (value, fallback) => {
  const n = Number(value);
  return Number.isFinite(n) && n >= 0 ? Math.floor(n) : fallback;
};

// Client-side display defaults. Keep in sync with Supabase Edge Function env vars:
// - powerup: SKIP_COST, INSPECT_COST, ZOOM_OUT_COST
// - riddle: RIDDLE_COST, RIDDLE_REPEAT_INCREMENT
export const POWERUP_COSTS = Object.freeze({
  // Display defaults (real enforcement is server-side). Keep these in sync with:
  // - supabase/functions/powerup (SKIP_COST, INSPECT_COST, ZOOM_OUT_COST)
  // - supabase/functions/riddle + api/riddle (RIDDLE_COST, RIDDLE_REPEAT_INCREMENT)
  skip: toPositiveInt(process.env.REACT_APP_SKIP_COST, 500),
  inspect: toPositiveInt(process.env.REACT_APP_INSPECT_COST, 100),
  zoom_out: toPositiveInt(process.env.REACT_APP_ZOOM_OUT_COST, 300),
  riddle_base: toPositiveInt(process.env.REACT_APP_RIDDLE_COST, RIDDLE_COST),
  riddle_early_guess_surcharge: toPositiveInt(
    process.env.REACT_APP_RIDDLE_EARLY_GUESS_SURCHARGE,
    RIDDLE_EARLY_GUESS_SURCHARGE
  ),
  riddle_easy_bonus: toPositiveInt(
    process.env.REACT_APP_RIDDLE_EASY_BONUS,
    RIDDLE_EASY_BONUS
  ),
  riddle_repeat_increment: toPositiveInt(
    process.env.REACT_APP_RIDDLE_REPEAT_INCREMENT,
    RIDDLE_REPEAT_INCREMENT
  ),
});

export const POWERUP_TYPES = Object.freeze({
  SKIP: 'skip',
  INSPECT: 'inspect',
  ZOOM_OUT: 'zoom_out',
  RIDDLE: 'riddle',
});

export const POWERUPS = Object.freeze({
  [POWERUP_TYPES.SKIP]: {
    type: POWERUP_TYPES.SKIP,
    label: 'Skip',
    uiCostCoins: POWERUP_COSTS.skip,
    info: 'Skip current and save streak.',
    usesFreeAllowance: true,
    freeRemainingField: 'free_skips_remaining',
  },
  [POWERUP_TYPES.INSPECT]: {
    type: POWERUP_TYPES.INSPECT,
    label: 'Inspect',
    uiCostCoins: POWERUP_COSTS.inspect,
    info: 'Get rid of letters on keyboard.',
    usesFreeAllowance: true,
    freeRemainingField: 'free_inspects_remaining',
  },
  [POWERUP_TYPES.ZOOM_OUT]: {
    type: POWERUP_TYPES.ZOOM_OUT,
    label: 'Zoom Out',
    uiCostCoins: POWERUP_COSTS.zoom_out,
    info: 'Fully zoom out briefly, then return to your previous zoom.',
    usesFreeAllowance: true,
    freeRemainingField: 'free_zoom_outs_remaining',
  },
  [POWERUP_TYPES.RIDDLE]: {
    type: POWERUP_TYPES.RIDDLE,
    label: 'Riddle',
    uiCostCoins: POWERUP_COSTS.riddle_base,
    uiRepeatIncrementCoins: POWERUP_COSTS.riddle_repeat_increment,
    info:
      'Generate a riddle for the current word. Each extra riddle for the same word costs more coins.',
  },
});

export const PROFILE_DEFAULTS = Object.freeze({
  // This is the amount new users start with (currently used by initializeProfile).
  // Intentionally hard-coded so new players always start with 250 coins.
  // If you want this configurable later, change BOTH this value and the DB default
  // migration that sets `public.profiles.coins` default.
  startingCoins: 250,
  freeSkipsRemaining: DEFAULT_FREE_SKIPS,
  freeInspectsRemaining: DEFAULT_FREE_INSPECTS,
  freeZoomOutsRemaining: 3,
  freeEasyRiddlesRemaining: 1,
});

// ---------------------------------------------------------------------------
// Coin rewards — now live in rewardValues.js (per-game-mode tables + daily cap).
// Re-exported here for backward compatibility with existing imports.
// ---------------------------------------------------------------------------
export {
  UNLIMITED_WIN_COINS,
  GENERATIONS_WIN_COINS,
  LOSS_REWARD_COINS,
  DAILY_COIN_CAP,
  getWinRewardCoins,
  getLossRewardCoins,
} from './rewardValues';

// [legacy] Old shared reward table kept as dead reference — DO NOT use.
// Pre-nerf values: 1:50, 2:30, 3:20, 4:15, 5:10, default:10, loss:5
const _OLD_WIN_REWARD_COINS = Object.freeze({
  // If you win in 1 guess, it’s rarer → bigger reward; later wins still get something.
  byGuessesUsed: Object.freeze({
    1: 50,
    2: 30,
    3: 20,
    4: 15,
    5: 10,
  }),
  default: 10,
});

// (getWinRewardCoins, getLossRewardCoins, LOSS_REWARD_COINS now re-exported from rewardValues.js above)

// Exchange rate messaging for the shop UI.
export const COIN_USD_EXCHANGE_RATE = Object.freeze({
  coinsPerUsd: 1000,
  usdPerCoin: 1 / 1000,
});

// Stripe coin packs (for future checkout flow).
// - `stripePriceId` should map to a Stripe Price (starts with "price_...").
// - Put them in .env as REACT_APP_STRIPE_PRICE_* so they’re easy to rotate.
export const COIN_PACKS = Object.freeze([
  {
    id: 'starter',
    label: 'Starter Pack',
    coins: 2000,
    price: { currency: 'usd', amountCents: 199 },
    stripePriceId: process.env.REACT_APP_STRIPE_PRICE_STARTER || null,
    bonusPct: 0,
    featured: false,
  },
  {
    id: 'pro',
    label: 'Pro Pack',
    coins: 6000,
    price: { currency: 'usd', amountCents: 499 },
    stripePriceId: process.env.REACT_APP_STRIPE_PRICE_PRO || null,
    bonusPct: 0.2,
    featured: true,
  },
  {
    id: 'creator',
    label: 'Creator Pack',
    coins: 15000,
    price: { currency: 'usd', amountCents: 999 },
    stripePriceId: process.env.REACT_APP_STRIPE_PRICE_CREATOR || null,
    bonusPct: 0.5,
    featured: false,
  },
]);

export const getCoinPackById = (packId) =>
  COIN_PACKS.find((p) => p.id === packId) || null;

